import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

export type RAGStatus = 'GREEN' | 'AMBER' | 'RED';

interface RAGBadgeProps {
  status: RAGStatus;
  percentageFilled?: number;
  showPercentage?: boolean;
  /** `dot` = coloured indicator only (no Green/Amber/Red text). */
  variant?: 'label' | 'dot';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusConfig: Record<RAGStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; bgClass: string }> = {
  GREEN: {
    label: 'Good',
    variant: 'default',
    bgClass: 'bg-rag-good text-white border-transparent',
  },
  AMBER: {
    label: 'Attention',
    variant: 'secondary',
    bgClass: 'bg-rag-warning text-white border-transparent',
  },
  RED: {
    label: 'Refill',
    variant: 'destructive',
    bgClass: 'bg-rag-critical text-white border-transparent',
  },
};

const dotAriaLabel: Record<RAGStatus, string> = {
  GREEN: 'Stock status: good',
  AMBER: 'Stock status: attention needed',
  RED: 'Stock status: critical',
};

export function RAGBadge({
  status,
  percentageFilled,
  showPercentage = false,
  variant = 'label',
  size = 'md',
  className,
}: RAGBadgeProps) {
  const config = statusConfig[status];

  if (variant === 'dot') {
    const dotSizes = { sm: 'size-2.5', md: 'size-3', lg: 'size-4' };
    const dotBg = {
      GREEN: 'bg-rag-good',
      AMBER: 'bg-rag-warning',
      RED: 'bg-rag-critical',
    }[status];
    return (
      <span
        role="img"
        aria-label={dotAriaLabel[status]}
        className={cn(
          'inline-block shrink-0 rounded-full ring-2 ring-background',
          dotSizes[size],
          dotBg,
          className
        )}
      />
    );
  }

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  const label = showPercentage && percentageFilled !== undefined
    ? `${Math.round(percentageFilled)}%`
    : config.label;

  return (
    <Badge
      variant={config.variant}
      className={cn(config.bgClass, sizeClasses[size], className)}
    >
      {label}
    </Badge>
  );
}

export default RAGBadge;
